import { type ChangeEventHandler } from 'react'

import { motion } from 'framer-motion'
import { CloseIcon } from '@chakra-ui/icons'
import {
  Avatar,
  Box,
  Button,
  Flex,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'

import { GetInterviewResponse } from '@pkg/types/api'
import { CandidateCVFile } from '@pkg/types/interview'

import { formatApplicationStage } from '@/helpers/ats'
import {
  useCandidate,
  useDeleteCandidateCV,
  useUploadCandidateCV,
  useUploadingCandidateCVNames,
} from '@/react-query/candidate'
import {
  INTERVIEW_KEY,
  useInterview,
  useUpdateInterview,
} from '@/react-query/interview/interview'
import { useIsViewOnlyMember } from '@/react-query/team'
import { LinkedinAvatarFallback } from '@/views/assets/LinkedinAvatarFallback'
import FileUploader from '@/views/components/FileUploader'
import { CreateCandidate } from '@/views/screens/Interview/components/CandidateCard/CreateCandidate/CreateCandidate'

import { CandidateAttachments } from '../CandidateAttachments'

import { EditCVModal } from './EditCVModal'
import { SelectPictureModal } from './SelectPictureModal'

const MotionBox = motion(Box)

export const EditCandidateWithAttachments = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isSelectPictureModalOpen,
    onOpen: onSelectPictureModalOpen,
    onClose: onSelectPictureModalClose,
  } = useDisclosure()

  const queryClient = useQueryClient()
  const toast = useToast()
  const candidateQuery = useCandidate()
  const candidate = candidateQuery.data
  const { data: interview } = useInterview()
  const updateInterview = useUpdateInterview()
  const uploadCandidateCV = useUploadCandidateCV()
  const deleteCandidateCV = useDeleteCandidateCV()
  const uploadingFileNames = useUploadingCandidateCVNames(candidate?.id)

  const isViewOnlyUser = useIsViewOnlyMember()

  if (!candidate && candidateQuery.isFetching) {
    return (
      <MotionBox
        mt={6}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minH="6rem"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <Flex align="center" gap={2} color="blackAlpha.600">
          <Spinner size="sm" />
          <Text fontSize="sm">Loading candidate…</Text>
        </Flex>
      </MotionBox>
    )
  }

  if (!candidate) {
    return null
  }

  const isUploading = uploadingFileNames.length > 0
  const hasAttachments = Boolean(candidate.CVs?.length) || isUploading
  // An empty candidate (no files, no CV text, not matched from an ATS) still
  // offers every way to add one; CreateCandidate has its own Edit button.
  const showsCreateCandidate =
    !hasAttachments && !candidate.cvText && interview?.matchStatus !== 'matched'

  const handleFileInputChange: ChangeEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (!files.length) {
      return
    }

    uploadCandidateCV
      .mutateAsync({ candidateId: candidate.id, files })
      .catch((error) => {
        console.error(error)
        toast({
          title: 'Could not upload the file',
          description: 'Please try again.',
          status: 'error',
          isClosable: true,
        })
      })
  }

  const handleRemoveFile = async (file: CandidateCVFile) => {
    if (!file.id) {
      return
    }

    // A candidate that exists only because of this file goes away with it:
    // the card returns to the empty state instead of keeping a name and the
    // "Add details" form. The file is deleted before the candidate is
    // unlinked, so the delete still passes the access check.
    const clearsCandidate =
      candidate.CVs?.length === 1 &&
      !isUploading &&
      !candidate.cvText &&
      !candidate.linkedInURL &&
      interview?.matchStatus !== 'matched'
    const interviewKey = [INTERVIEW_KEY, interview?.id]

    if (clearsCandidate) {
      queryClient.setQueryData(
        interviewKey,
        (oldInterview?: GetInterviewResponse['interview']) =>
          oldInterview && { ...oldInterview, candidateId: null },
      )
    }

    try {
      await deleteCandidateCV.mutateAsync({
        candidateId: candidate.id,
        cvId: file.id,
      })
      if (clearsCandidate) {
        await updateInterview.mutateAsync({ candidateId: null })
      }
    } catch (error) {
      console.error(error)
      if (clearsCandidate) {
        void queryClient.invalidateQueries({ queryKey: interviewKey })
      }
      toast({
        title: 'Could not remove the file',
        description: 'Please try again.',
        status: 'error',
        isClosable: true,
      })
    }
  }

  return (
    <>
      <SelectPictureModal
        isOpen={isSelectPictureModalOpen}
        onClose={onSelectPictureModalClose}
      />
      <MotionBox
        as={Box}
        display="flex"
        flexDirection="column"
        position="relative"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center mt-6">
          <Box
            position="relative"
            display="inline-block"
            onClick={onSelectPictureModalOpen}
            cursor={isViewOnlyUser ? 'default' : 'pointer'}
          >
            <Avatar
              src={candidate.profilePictureURL || undefined}
              className="w-12 h-12"
              icon={<LinkedinAvatarFallback h={12} w={12} rounded={'full'} />}
            />

            {!isViewOnlyUser && (
              <Box
                position="absolute"
                top={0}
                left={0}
                bottom={0}
                right={0}
                bg="transparent"
                color="transparent"
                rounded={'full'}
                alignItems="center"
                justifyContent="center"
                display="flex"
                _hover={{ bg: 'blackAlpha.300', color: 'white' }}
                transition={'all 0.2s ease-in-out'}
              >
                <Text fontWeight="bold">Edit</Text>
              </Box>
            )}
          </Box>
          <div className="ml-3">
            {candidate.fullName ? (
              <Text fontWeight={'bold'} fontSize={'1.25rem'}>
                {candidate.fullName}
              </Text>
            ) : (
              <Text fontWeight={'bold'} fontSize={'1.25rem'} color="gray.400">
                {isUploading ? 'Reading CV…' : 'No name'}
              </Text>
            )}
            {(interview?.applicationStage || interview?.appliedAt) && (
              <Text
                fontSize={'0.75rem'}
                color={'gray.500'}
                fontWeight={'500'}
                mt={'1px'}
              >
                {interview.applicationStage &&
                  formatApplicationStage(interview.applicationStage)}
                {interview.applicationStage && interview.appliedAt && ' · '}
                {interview.appliedAt &&
                  `Applied ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(interview.appliedAt))}`}
              </Text>
            )}
          </div>

          {!isViewOnlyUser && (
            <IconButton
              variant={'brand-solid-white'}
              rounded={'full'}
              ml={3}
              aria-label={'Remove Candidate'}
              icon={<CloseIcon />}
              onClick={async () => {
                updateInterview.mutate({
                  candidateId: null,
                })
              }}
            />
          )}
        </div>
        {candidate.linkedInURL && (
          <Button
            variant={'brand-solid-white'}
            as={'a'}
            href={candidate.linkedInURL}
            target="_blank"
            rel="nofollow noopener noreferrer"
            mt={5}
            alignSelf={'flex-start'}
            rounded={'full'}
          >
            View on LinkedIn &nbsp;&nbsp;&nbsp;
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
            >
              <path
                d="M1.19935 1.75313L12.1993 1.75313"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M12.658 13.3232L12.6579 2.33958"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <line
                x1="1"
                y1="13.4139"
                x2="12.5966"
                y2="1.81736"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </Button>
        )}

        <CandidateAttachments
          files={candidate.CVs}
          uploadingFileNames={uploadingFileNames}
          canManage={!isViewOnlyUser}
          onRemove={(file) => void handleRemoveFile(file)}
          mt={5}
        />

        {showsCreateCandidate ? (
          <>
            <AddDetailsDivider />
            <CreateCandidate />
          </>
        ) : (
          <Flex mt={5} gap={3} wrap="wrap">
            {(!isViewOnlyUser || candidate.cvText) && (
              <Button
                variant={'brand-solid-white'}
                onClick={(event) => {
                  event.preventDefault()
                  onOpen()
                }}
              >
                {isViewOnlyUser ? 'View' : 'Edit...'}
              </Button>
            )}
            {!isViewOnlyUser && (
              <FileUploader
                name="candidateFiles"
                id="candidateFiles"
                acceptedFileTypes=".pdf,.doc,.docx"
                onFileChange={handleFileInputChange}
                inputProps={{ multiple: true }}
              >
                {hasAttachments ? 'Add more files...' : 'Upload files...'}
              </FileUploader>
            )}
          </Flex>
        )}
      </MotionBox>
      <EditCVModal isOpen={isOpen} onClose={onClose} />
    </>
  )
}

const AddDetailsDivider = () => {
  return (
    <div className="mt-6 flex">
      <span className="inset-0 flex-grow flex items-center">
        <div className="w-full border-t border-[#00000033]" />
      </span>
      <div className="relative flex justify-center text-md leading-5 text-black">
        <span className="px-2">Add details</span>
      </div>
      <span className="inset-0 flex-grow flex items-center">
        <div className="w-full border-t border-[#00000033]" />
      </span>
    </div>
  )
}
